import * as Tone from 'tone';
import type { FaseTrack } from './audio/beats/fases';
import { loadManifest, loadTrack, musicFormats, trackOf, type LoadedTrack, type MusicManifest } from './audio/track';
import { boardSeed, type KeyValueStore } from './core/progress';
import { dailySeed } from './core/rng';
import { accuracy, meanErrorMs } from './core/score';
import { clearFase, faseById, faseCleared, faseTitle, nextFase, startFase, worldList, faseLook } from './game/campaign';
import type { Cue } from './game/cue';
import { createGame, type GameHud } from './game/loop';
import { hexOf, lookFrom, lookOf, type Look, type LookId } from './render/look';
import './ui/looks.css';
import { createSite, type Site } from './ui/site';
import { createClipRecorder, type ClipRecorder } from './share/clip';

const el = (id: string): HTMLElement => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node;
};

const scoreEl = el('score');
const metaEl = el('meta');
const livesEl = el('lives');
const verdictEl = el('verdict');
const overEl = el('over');
const hudEl = el('hud');
const introEl = el('intro');
const menuEl = el('menu');
const cueEl = el('cue');

const seed = new URLSearchParams(location.search).get('seed') ?? dailySeed();
const fonts = new Map<string, Promise<void>>();

function loadFont(look: Look): Promise<void> {
  const known = fonts.get(look.fontFile);
  if (known !== undefined) return known;
  const loading = new FontFace(look.font, `url(./fonts/${look.fontFile})`, { weight: look.fontWeight, display: 'swap' })
    .load()
    .then((face) => {
      document.fonts.add(face);
    })
    .catch(() => undefined);
  fonts.set(look.fontFile, loading);
  return loading;
}

function applyLook(id: LookId): Look {
  const look = lookOf(id);
  document.documentElement.dataset.look = look.id;
  document.documentElement.style.setProperty('--paper', hexOf(look.palette.bg));
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', hexOf(look.palette.bg));
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', look.dark ? 'dark' : 'light');
  void loadFont(look);
  return look;
}

function safeStore(): KeyValueStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const store = safeStore();
const firstFase = startFase(location.search, store);
const lookFor = (f: FaseTrack): LookId => lookFrom(location.search, faseLook(f));
const lesson = (f: FaseTrack): boolean => f.coach > 0 && !faseCleared(store, f);
const look = applyLook(lookFor(firstFase));

const MUSIC_CACHE = 2;
const manifest: Promise<MusicManifest | null> = loadManifest().catch(() => null);
const tracks = new Map<string, Promise<LoadedTrack | null>>();

function music(f: FaseTrack): Promise<LoadedTrack | null> {
  const known = tracks.get(f.id);
  if (known !== undefined) {
    tracks.delete(f.id);
    tracks.set(f.id, known);
    return known;
  }
  const raw = Tone.getContext().rawContext as unknown as BaseAudioContext;
  const formats = musicFormats((type) => new Audio().canPlayType(type));
  const loading = manifest
    .then((m) => (m === null ? null : loadTrack(trackOf(m, f.id), raw, { formats })))
    .catch(() => {
      tracks.delete(f.id);
      return null;
    });
  tracks.set(f.id, loading);
  for (const id of tracks.keys()) {
    if (tracks.size <= MUSIC_CACHE) break;
    tracks.delete(id);
  }
  return loading;
}

const LABEL: Record<string, string> = {
  perfect: 'perfeito',
  good: 'bom',
  weak: 'fraco',
  flop: 'flop',
  miss: 'passou',
};

let shown = -1;
let wantsIntro = true;
let last: GameHud | null = null;
let site: Site | null = null;
let wasFresh = true;
let sent = false;

const CLIP_MULTIPLIER = 8;
let clip: ClipRecorder | null = null;
let clipReached = false;
let clipBusy = false;

const clipBtn = document.createElement('button');
clipBtn.id = 'clip';
clipBtn.type = 'button';
clipBtn.textContent = 'clipe';
el('ui').appendChild(clipBtn);

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const start = performance.now();
    function tick(now: number): void {
      if (now - start >= ms) resolve();
      else requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  });
}

function syncClipButton(): void {
  const visible = clip !== null && clipReached;
  clipBtn.classList.toggle('on', visible);
  clipBtn.disabled = !visible || clipBusy;
}

clipBtn.addEventListener('click', () => {
  if (clip === null || clipBusy) return;
  clipBusy = true;
  clipBtn.textContent = 'salvando…';
  syncClipButton();
  void clip
    .save()
    .then(async (ok) => {
      clipBtn.textContent = ok ? 'salvo!' : 'falhou';
      await delay(1600);
      clipBtn.textContent = 'clipe';
    })
    .finally(() => {
      clipBusy = false;
      syncClipButton();
    });
});

const classes = (...names: (string | false)[]): string => names.filter(Boolean).join(' ');
let viewWorld = firstFase.world;

function renderPicker(current: FaseTrack): void {
  const list = worldList(store, current);
  const view = list.find((entry) => entry.world.n === viewWorld) ?? list[0];
  if (view === undefined) return;
  const tabs = list
    .map(
      (entry) =>
        `<button type="button" class="${classes('wld', entry === view && 'on', !entry.unlocked && 'locked')}" data-world="${entry.world.n}" aria-pressed="${String(entry === view)}"><b>${entry.world.n}</b><span>${entry.world.name}</span></button>`,
    )
    .join('');
  const chips = view.fases
    .map(({ fase, unlocked, current: on }) => {
      const tag = fase.boss ? 'chefe' : unlocked ? '' : 'trancado';
      const cls = classes('lvl', on && 'on', !unlocked && 'locked', fase.boss && 'boss');
      return `<button type="button" class="${cls}" data-fase="${fase.id}"${unlocked ? '' : ' disabled'}><b>${fase.fase}</b>${fase.faseName}${tag ? `<small>${tag}</small>` : ''}</button>`;
    })
    .join('');
  const html = `<div class="worlds" role="group" aria-label="mundos">${tabs}</div><div class="fases" role="group" aria-label="fases">${chips}</div>`;
  for (const box of document.querySelectorAll<HTMLElement>('.levels')) box.innerHTML = html;
}

const coarsePointer = (): boolean => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

function overText(hud: GameHud): string {
  const { state } = hud;
  const touch = coarsePointer();
  const stats = `${state.score} PTS · ${(accuracy(state) * 100).toFixed(0)}% · ERRO MÉDIO ${meanErrorMs(state).toFixed(0)} MS`;
  if (!hud.cleared) return `${stats} · ${touch ? 'TOQUE' : 'ESPAÇO'} PRA TENTAR DE NOVO`;
  const next = nextFase(hud.fase);
  if (next !== null) {
    const opened =
      next.world === hud.fase.world
        ? `F${next.fase} ${next.faseName.toUpperCase()} LIBERADA`
        : `MUNDO ${next.world} · ${next.worldName.toUpperCase()} LIBERADO`;
    return `${opened} · ${stats} · ${touch ? 'TOQUE PRA SEGUIR' : 'ESPAÇO SEGUE · R REPETE'}`;
  }
  return `${faseTitle(hud.fase).toUpperCase()} COMPLETA · ${stats} · ${touch ? 'TOQUE' : 'ESPAÇO'} REPETE`;
}

let listedFase = '';

function render(hud: GameHud): void {
  const { state } = hud;
  last = hud;
  if (hud.fase.id !== listedFase) {
    listedFase = hud.fase.id;
    viewWorld = hud.fase.world;
    renderPicker(hud.fase);
  }
  const fresh = state.judged === 0 && state.score === 0 && !state.over;
  if (fresh && !wasFresh) {
    clipReached = false;
    clip?.disarm();
    sent = false;
    site?.startRun();
  }
  wasFresh = fresh;
  if (!hud.running && !hud.calibrating && (state.over || hud.cleared) && !sent && state.judged > 0) {
    sent = true;
    site?.offerRun({
      seed: hud.seed,
      score: state.score,
      judged: state.judged,
      perfects: state.counts.perfect,
      accuracy: accuracy(state),
      errorMs: Math.abs(meanErrorMs(state)),
      multiplier: state.maxMultiplier,
    });
  }
  if (state.multiplier >= CLIP_MULTIPLIER) {
    clipReached = true;
    clip?.arm();
  }
  syncClipButton();
  if (hud.running) wantsIntro = false;
  const intro = wantsIntro && !hud.running && !hud.calibrating;
  introEl.classList.toggle('on', intro);
  introEl.classList.toggle('first', hud.fase.safe && lesson(hud.fase));
  menuEl.classList.toggle('on', !hud.running && !hud.calibrating && !intro);
  scoreEl.textContent = state.score.toLocaleString('pt-BR');
  metaEl.innerHTML = `<b class="m${state.multiplier}">×${state.multiplier}</b>${state.streak} seguidas · ${hud.fase.worldName} F${hud.fase.fase} · ${hud.fase.faseName}`;
  livesEl.innerHTML = '♥'.repeat(Math.max(0, state.lives)) + '<i>♥</i>'.repeat(Math.max(0, 3 - state.lives));

  if (hud.verdict === null) {
    verdictEl.textContent = '';
    shown = -1;
  } else if (state.judged !== shown) {
    shown = state.judged;
    const sign = hud.deltaMs >= 0 ? '+' : '';
    const combo = hud.milestone ? ` · ${hud.milestone} seguidas` : '';
    verdictEl.className = hud.verdict;
    verdictEl.innerHTML = `${LABEL[hud.verdict] ?? hud.verdict}<small>${sign}${hud.deltaMs.toFixed(0)} ms${combo}</small>`;
    verdictEl.style.animation = 'none';
    void verdictEl.offsetHeight;
    verdictEl.style.animation = '';
  }

  overEl.classList.toggle('end', !hud.running && state.judged > 0);
  if (hud.loading) {
    overEl.textContent = 'CARREGANDO A MÚSICA…';
  } else if (hud.running || hud.calibrating || intro) {
    overEl.textContent = '';
  } else if (state.judged > 0) {
    overEl.textContent = overText(hud);
  } else {
    const start = coarsePointer() ? 'TOQUE PRA COMEÇAR' : 'ESPAÇO OU TOQUE PRA COMEÇAR  ·  C CALIBRA';
    const boss = hud.fase.boss ? ' · CHEFE' : '';
    const how =
      hud.fase.fase === 1 && lesson(hud.fase)
        ? 'SEGURA E SOLTA NO SOLTA! — A GENTE TE MOSTRA QUANDO'
        : 'SEGURA E ARRASTA ATÉ A SOMBRA, SOLTA QUANDO O CÍRCULO POUSAR';
    overEl.textContent = `${faseTitle(hud.fase).toUpperCase()}${boss} — ${how}  ·  ${start}`;
  }

  const note = hud.note;
  const signed = (value: number, digits: number): string => `${value >= 0 ? '+' : ''}${value.toFixed(digits)}`;
  const sync =
    hud.syncMs === null || hud.syncNote === null
      ? '—'
      : `apex #${hud.syncNote} áudio−juiz ${signed(hud.syncMs, 1)} ms · último Δ ${signed(hud.deltaMs, 0)} ms`;
  hudEl.textContent = [
    `seed     ${hud.seed}  ·  ${hud.bpm} bpm  ·  beat ${hud.beat.toFixed(1)}  ·  ${hud.music ?? '—'}`,
    `note     ${note ? `#${note.index} ${note.shape} ${note.size.toFixed(2)} apex ${note.apexBeat}b hold ${note.holdBeats}b${note.blind ? ' cego' : ''}` : '—'}`,
    `latency  ${hud.outputLatencyMs.toFixed(1)} ms  ·  calib ${hud.offsetMs >= 0 ? '+' : ''}${hud.offsetMs.toFixed(1)} ms`,
    `acc      ${(accuracy(state) * 100).toFixed(1)}%  ·  erro ${meanErrorMs(state).toFixed(1)} ms`,
    `hold     ${hud.holding ? 'segurando' : 'solto'}`,
    `sync     ${sync}`,
  ].join('\n');
}

function showCue(cue: Cue | null): void {
  cueEl.className = '';
  cueEl.textContent = cue?.text ?? '';
  if (cue === null) return;
  void cueEl.offsetHeight;
  cueEl.className = `on ${cue.kind}`;
}

await Promise.race([loadFont(look), delay(1500)]);

const game = await createGame(el('stage'), {
  seed,
  fase: firstFase,
  onHud: render,
  onClear: (fase) => {
    clearFase(store, fase);
    listedFase = '';
  },
  onCue: showCue,
  coach: lesson,
  blocked: () => site?.blocked() === true,
  music,
  lookOf: lookFor,
  probe: new URLSearchParams(location.search).has('gate'),
  onLook: (id) => {
    applyLook(id);
  },
});

function runCalibration(): void {
  void game.calibrate().then((result) => {
    verdictEl.className = result === null ? 'miss' : 'perfect';
    verdictEl.innerHTML =
      result === null
        ? 'sem dados<small>bate junto com o click até o fim</small>'
        : `calibrado<small>${result.offsetMs >= 0 ? '+' : ''}${result.offsetMs.toFixed(0)} ms · σ ${result.spreadMs.toFixed(0)} ms · ${result.used} toques</small>`;
  });
}

function toggleIntro(): void {
  wantsIntro = !wantsIntro;
  if (last !== null) render(last);
}

site = createSite({
  seed: () => last?.seed ?? boardSeed(seed, firstFase.id),
  onPlay: () => {
    wantsIntro = false;
    void game.restart();
  },
  onCalibrate: runCalibration,
  onHow: toggleIntro,
});

const canvas = document.querySelector('canvas');
if (canvas !== null) {
  clip = createClipRecorder(canvas, Tone.getDestination().output.output);
  syncClipButton();
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const tab = target.closest<HTMLButtonElement>('[data-world]');
  if (tab !== null) {
    viewWorld = Number(tab.dataset.world);
    tab.blur();
    renderPicker(last?.fase ?? firstFase);
    return;
  }
  const pick = target.closest<HTMLButtonElement>('[data-fase]');
  const fase = faseById(pick?.dataset.fase);
  if (pick === null || pick.disabled || fase === null) return;
  wantsIntro = false;
  game.setFase(fase);
  pick.blur();
  void game.restart();
});

addEventListener('keydown', (event) => {
  if (site?.blocked() === true) return;
  if (event.code === 'KeyR') void game.restart();
  if (event.code === 'KeyH') hudEl.classList.toggle('off');
  if (event.code === 'KeyI') toggleIntro();
  if (event.code === 'KeyL') site?.openBoard();
  if (event.code === 'KeyC') runCalibration();
});
